import React, { useState, useEffect, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faUsers,
  faSearch,
  faSpinner,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import MessageCard from "./MessageCard";
import { clientAdminService } from "../../../Services/clientAdminService";
import {
  ClientSearchInput,
  ResultsList,
  ResultItem,
  SearchAndSelectContainer,
  SelectedClientsList,
  ClientTag,
} from "./CommunicationsCenterStyle";
import useDebounce from "../../../hooks/useDebounce";

// --- ESTILOS COMPLETOS PARA O MODAL ---

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const scaleUp = keyframes`from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; }`;

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 600px;
  animation: ${scaleUp} 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  h2 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e293b;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #64748b;
  font-size: 1.5rem;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
`;

const Section = styled.div``;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #475569;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid ${(props) => (props.checked ? "#3b82f6" : "#e2e8f0")};
  background-color: ${(props) => (props.checked ? "#eff6ff" : "transparent")};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  input {
    margin-right: 12px;
  }
`;

const Summary = styled.div`
  background: #f8fafc;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background-color: #f8fafc;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${(props) => (props.primary ? "#3b82f6" : "#cbd5e1")};
  background-color: ${(props) => (props.primary ? "#3b82f6" : "white")};
  color: ${(props) => (props.primary ? "white" : "#475569")};
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-top: 1px solid #e2e8f0;
  font-size: 0.85rem;
  color: #64748b;
`;

const PaginationButton = styled.button`
  background: none;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 4px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// --- COMPONENTE PRINCIPAL ---

export default function SendModal({ onClose, onConfirmSend, message }) {
  const [targetAudience, setTargetAudience] = useState("ALL");
  const [selectedClients, setSelectedClients] = useState([]);

  // --- Estados de Busca e Paginação ---
  const [clientSearch, setClientSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(5);
  const [isSearching, setIsSearching] = useState(false);

  const debouncedSearchTerm = useDebounce(clientSearch, 500);

  const searchForClients = useCallback(
    async (term, page) => {
      if (term.trim().length < 2) {
        setSearchResults([]);
        setTotalPages(1);
        setCurrentPage(1);
        return;
      }
      setIsSearching(true);
      const offset = (page - 1) * itemsPerPage;
      try {
        const response = await clientAdminService.searchClients(
          itemsPerPage,
          offset,
          term
        );

        // Lógica robusta para tratar a resposta da API
        const results = Array.isArray(response)
          ? response
          : response.clients || [];
        const total = response.totalCount || results.length;

        const existingIds = new Set(selectedClients.map((c) => c.id));
        setSearchResults(results.filter((r) => !existingIds.has(r.id)));
        setTotalPages(Math.ceil(total / itemsPerPage));
      } catch (error) {
        console.error("Falha na busca de clientes", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [selectedClients, itemsPerPage]
  );

  useEffect(() => {
    setCurrentPage(1);
    searchForClients(debouncedSearchTerm, 1);
  }, [debouncedSearchTerm, searchForClients]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      searchForClients(debouncedSearchTerm, newPage);
    }
  };

  const handleSelectClient = (client) => {
    setSelectedClients((prev) => [...prev, client]);
    setClientSearch("");
    setSearchResults([]);
    setTotalPages(1);
    setCurrentPage(1);
  };

  const handleRemoveClient = (clientId) => {
    setSelectedClients((prev) => prev.filter((c) => c.id !== clientId));
  };

  const isConfirmDisabled = useMemo(() => {
    return targetAudience === "SPECIFIC" && selectedClients.length === 0;
  }, [targetAudience, selectedClients]);

  const handleConfirm = () => {
    if (isConfirmDisabled) {
      alert(
        "Por favor, selecione pelo menos um cliente para o envio específico."
      );
      return;
    }
    const sendOptions = {
      targetAudience,
      specificClientIds:
        targetAudience === "SPECIFIC" ? selectedClients.map((c) => c.id) : null,
    };
    onConfirmSend(sendOptions);
    onClose();
  };

  return ReactDOM.createPortal(
    <ModalBackdrop>
      <ModalContent>
        <ModalHeader>
          <h2>Enviar Mensagem</h2>
          <CloseButton onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          <Section>
            <SectionTitle>
              <FontAwesomeIcon icon={faUsers} /> Selecione o Público
            </SectionTitle>
            <RadioGroup>
              <RadioLabel checked={targetAudience === "ALL"}>
                <input
                  type="radio"
                  name="target"
                  value="ALL"
                  checked={targetAudience === "ALL"}
                  onChange={(e) => setTargetAudience(e.target.value)}
                />{" "}
                Todos os Clientes
              </RadioLabel>
              <RadioLabel checked={targetAudience === "ACTIVE_CONTRACTS"}>
                <input
                  type="radio"
                  name="target"
                  value="ACTIVE_CONTRACTS"
                  checked={targetAudience === "ACTIVE_CONTRACTS"}
                  onChange={(e) => setTargetAudience(e.target.value)}
                />{" "}
                Clientes com Contratos Ativos
              </RadioLabel>
              <RadioLabel checked={targetAudience === "SPECIFIC"}>
                <input
                  type="radio"
                  name="target"
                  value="SPECIFIC"
                  checked={targetAudience === "SPECIFIC"}
                  onChange={(e) => setTargetAudience(e.target.value)}
                />{" "}
                Clientes Específicos
              </RadioLabel>
            </RadioGroup>

            {targetAudience === "SPECIFIC" && (
              <>
                <SearchAndSelectContainer>
                  <ClientSearchInput>
                    <FontAwesomeIcon icon={faSearch} />
                    <input
                      type="text"
                      placeholder="Buscar cliente por nome ou CPF..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                    />
                    {isSearching && <FontAwesomeIcon icon={faSpinner} spin />}
                  </ClientSearchInput>
                  {searchResults.length > 0 ? (
                    <ResultsList>
                      {searchResults.map((client) => (
                        <ResultItem
                          key={client.id}
                          onClick={() => handleSelectClient(client)}
                        >
                          {client.name} ({client.cpfCnpj})
                        </ResultItem>
                      ))}
                    </ResultsList>
                  ) : (
                    debouncedSearchTerm.length >= 2 &&
                    !isSearching && (
                      <ResultItem
                        style={{ cursor: "default", color: "#9ca3af" }}
                      >
                        Nenhum cliente encontrado.
                      </ResultItem>
                    )
                  )}
                  {totalPages > 1 && (
                    <PaginationControls>
                      <PaginationButton
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </PaginationButton>
                      <span>
                        Página {currentPage} de {totalPages}
                      </span>
                      <PaginationButton
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </PaginationButton>
                    </PaginationControls>
                  )}
                </SearchAndSelectContainer>

                {selectedClients.length > 0 && (
                  <SelectedClientsList>
                    {selectedClients.map((client) => (
                      <ClientTag key={client.id}>
                        {client.name}
                        <button onClick={() => handleRemoveClient(client.id)}>
                          &times;
                        </button>
                      </ClientTag>
                    ))}
                  </SelectedClientsList>
                )}
              </>
            )}
          </Section>
          <Summary>
            <SectionTitle>Revisão</SectionTitle>
            <MessageCard message={message} isPreview />
          </Summary>
        </ModalBody>
        <ModalFooter>
          <ActionButton onClick={onClose}>Cancelar</ActionButton>
          <ActionButton
            primary
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            Enviar Agora
          </ActionButton>
        </ModalFooter>
      </ModalContent>
    </ModalBackdrop>,
    document.body
  );
}
